import { useCallback, useMemo, useRef } from 'react'
import { Flex, Box } from 'theme-ui'
import { Formik, Form, FormikProps } from 'formik'
import { Paper, Heading, Text, Button, Loading } from 'components'
import { TeacherFormSchema } from 'components/Form'
import { noop } from 'utils'
import { useHandleEscapeKey } from 'hooks'
import { ConfigUtils, FormikOnlyValues, TeacherCreateFormValues } from 'types'
import { hasChanged } from 'utils/helpers'
import { IOS, ANDROID, TEACHER, TEACHER_CREATE } from '../../constants'
import { TeacherTrainingSection } from '../FormSections'
import { useConfirmation } from 'hooks/useConfirmation'
import getLocalized from 'i18n'

const localized = getLocalized()

type FormValues = Partial<TeacherCreateFormValues> & FormikOnlyValues

export type CreateTeacherFormProps = {
  isSupport?: boolean
  disabled?: boolean
  pending?: boolean
  responseError?: string
  configUtils: ConfigUtils
  onFormCancel?: () => void
  onFormSubmit: (values: TeacherCreateFormValues, fullname?: string) => void
}

function getCreateTeacherValues({ values }: { values: FormValues }) {
  const formValues = {
    ...values,
  }
  const {
    __country,
    __teachingOrg,
    __teachingOrgId,
    ...filteredValues
  } = formValues

  return filteredValues as TeacherCreateFormValues
}

export function CreateTeacherForm({
  isSupport = false,
  responseError,
  configUtils,
  disabled = false,
  pending = false,
  onFormSubmit,
  onFormCancel = noop,
}: CreateTeacherFormProps) {
  const initialValues = useMemo(
    () =>
      ({
        forename: '',
        surname: '',
        email: '',
        instructionDate: '',
        countryCode: undefined,
        device: undefined,
        isTester: 'no',
        userType: TEACHER,
      } as FormValues),
    []
  )
  const formikRef = useRef<FormikProps<FormValues>>(null)

  const hasFormChanged = useCallback(() => {
    const form = formikRef.current
    if (form) {
      const values = form.values
      return hasChanged({ values, initialValues })
    } else {
      return false
    }
  }, [formikRef, initialValues])

  const confirmCancel = useConfirmation({
    header: localized.confirmationModal.closeWithoutSaving.header,
    body: localized.confirmationModal.closeWithoutSaving.body,
    shouldConfirm: hasFormChanged,
  })

  const cancel = useCallback(async () => {
    const form = formikRef.current
    if (form) {
      if (await confirmCancel()) {
        form.handleReset()
        onFormCancel()
      }
    }
  }, [formikRef, onFormCancel, confirmCancel])

  useHandleEscapeKey(cancel)

  const handleSubmit = (values: FormValues) => {
    // Create student
    const filteredValues = getCreateTeacherValues({ values })
    //if no device selected and it's a public country, set device to ios for backend validation
    if (
      !filteredValues.device &&
      configUtils.isCountryPublic(filteredValues.countryCode)
    ) {
      filteredValues.device = IOS
    }
    if (filteredValues.device !== ANDROID) {
      filteredValues.googlePlayEmail = undefined
    }
    onFormSubmit(filteredValues)
  }
  return (
    <Box sx={{ width: '100%' }} data-cy="teacher-create-form">
      <Flex variant="userProfile.form">
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          enableReinitialize
          onSubmit={handleSubmit}
          validationSchema={TeacherFormSchema}>
          {({ touched, handleSubmit }: FormikProps<FormValues>) => {
            return (
              <Form
                onSubmit={handleSubmit}
                autoComplete="off"
                autoCorrect="off">
                <Box sx={{ py: 6 }}>
                  <Heading variant="forms.h1" sx={{ textAlign: 'left' }}>
                    Add a new teacher
                  </Heading>
                </Box>
                <Box variant={`forms.section.header`}>
                  <Heading variant="forms.h2" sx={{ display: 'inline-block' }}>
                    Teacher information
                  </Heading>
                </Box>
                <Flex variant={`forms.section.edit`}>
                  <TeacherTrainingSection
                    viewMode="edit"
                    isDisabledField={() => disabled}
                    configUtils={configUtils}
                    shouldShowError={(field) =>
                      !!touched[field as keyof FormValues]
                    }
                    startTabIndex={1}
                    isSupport={isSupport}
                    pageContext={TEACHER_CREATE}
                  />
                </Flex>

                <Box sx={{ mt: 8 }}>
                  {responseError && (
                    <Paper sx={{ boxShadow: '0 0 0 2px #fbebef' }}>
                      <Flex
                        sx={{
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        <Text sx={{ fontWeight: 'heading', color: '#f18aa3' }}>
                          {responseError}
                        </Text>
                      </Flex>
                    </Paper>
                  )}
                </Box>

                <Flex
                  variant="forms.buttons.container"
                  sx={{ mt: 45, mb: 165 }}>
                  <Button
                    variant="forms.buttons.primary"
                    data-cy="submit"
                    type="submit"
                    disabled={disabled || !hasFormChanged()}>
                    {pending ? (
                      <Loading />
                    ) : (
                      <>
                        <span>Save</span>
                      </>
                    )}
                  </Button>
                  <Button
                    variant="forms.buttons.secondary"
                    type="button"
                    data-cy="cancel"
                    disabled={disabled}
                    onClick={cancel}>
                    Cancel
                  </Button>
                </Flex>
              </Form>
            )
          }}
        </Formik>
      </Flex>
    </Box>
  )
}
